import { useConversations } from '../../contexts/CommunicationNavigationContext';
import { useEffect, useState } from 'react';
import TypeAhead from '../TypeAhead';
import { Drawer } from '../ui/Drawer';
import { communications, communicationError, refreshCommunications, type CommunicationAudience, type Conversation } from '../../services/communications.service';

const inputClass = 'w-full rounded border border-theme-border-primary bg-panel px-3 py-2 text-body';
const primaryClass = 'rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-40';
interface Props { pro: string; conversationId?: string; onClose: () => void }
export default function CommunicationDrawer({ pro, conversationId, onClose }: Props) {
  const { rows } = useConversations();
  const latest = rows.find(row => row.conversation_id === conversationId);
  const [options, setOptions] = useState<CommunicationAudience[]>([]);
  const [target, setTarget] = useState('');
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState<Conversation>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true);
    const load = async () => {
      try {
        if (conversationId) {
          const row = await communications.detail(conversationId);
          if (!active) return;
          setConversation(row);
          await communications.read(conversationId, row.last_message_id);
          if (active) refreshCommunications();
        } else {
          const audience = await communications.audience(pro);
          if (active) setOptions(audience);
        }
      } catch (e) { if (active) setError(communicationError(e)); }
      finally { if (active) setLoading(false); }
    };
    void load();
    return () => { active = false; };
  }, [conversationId, pro, latest?.last_message_id, latest?.status]);
  const save = async (resolve = false) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      if (conversationId) {
        const row = resolve ? await communications.resolve(conversationId) : await communications.reply(conversationId, message.trim());
        setConversation(row); setMessage('');
        refreshCommunications();
      } else {
        await communications.create(pro, target, message.trim());
        refreshCommunications(); onClose();
      }
    } catch (e) { setError(communicationError(e)); }
    finally { setBusy(false); }
  };
  return <Drawer isOpen onClose={onClose} onBeforeClose={() => !busy && (!message.trim() || window.confirm('Discard your unsent message?'))} title={`Communication Request · PRO ${pro}`} maxWidth="xl"
    footer={<div className="flex justify-end gap-2">
      {conversation?.status === 'OPEN' && (conversation.is_recipient || conversation.is_sender) && <button type="button" disabled={busy || loading || !!message.trim()} className="rounded border border-theme-border-primary px-3 py-2 text-sm text-body disabled:opacity-40" onClick={() => void save(true)}>Mark resolved</button>}
      {conversation?.status !== 'RESOLVED' && <button type="button" className={primaryClass} disabled={busy || loading || !message.trim() || (!conversationId && !target) || (!!conversationId && !conversation)} onClick={() => void save()}>{busy ? 'Saving…' : conversationId ? (conversation?.is_recipient ? 'Send reply' : 'Add Additional Comment') : 'Send request'}</button>}
    </div>}>
    <div className="space-y-4">
      {loading && <p role="status">Loading communication…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {!conversationId && <TypeAhead label="Target Audience" ariaLabel="Target Audience" value={target} onChange={setTarget} options={options} showDescriptions disabled={busy || loading} placeholder="Choose a role or search users…" />}
      {conversation && <>
        <p className="text-sm text-muted">{conversation.sender_name} → {conversation.recipient_name} · {conversation.status === 'RESOLVED' ? 'Resolved' : 'Open'}</p>
        <ol className="space-y-3">{conversation.messages?.map(note => <li key={note.id} className="rounded border border-theme-border-primary p-3">
          <p className="text-xs text-muted">{note.author_name} · {new Date(note.created_at).toLocaleString()}</p>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-body">{note.message}</p>
        </li>)}</ol>
      </>}
      {conversation?.status !== 'RESOLVED' && <label className="block text-sm text-body">{conversationId ? (conversation?.is_recipient ? 'Reply' : 'Add Additional Comment') : 'Message'}<textarea className={`${inputClass} mt-1`} rows={6} maxLength={10000} value={message} onChange={e => setMessage(e.target.value)} disabled={busy || loading} /></label>}
    </div>
  </Drawer>;
}
